import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Configuration } from 'src/app/app.constants';
import {
  IVenueEventAlertType,
  IVenueEventDetail,
  IVenueEventListFilter,
  IVenueEventListItem,
  IVenueEventSaveRequest,
  IVenueEventSensorTypeOptions,
  IVenueEventSystemEventOption,
  IVenueEventTaskOption,
} from 'src/app/core/models/venue-event.model';

@Injectable()
export class VenueEventHttpService {
  constructor(
    private readonly http: HttpClient,
    private readonly configuration: Configuration,
  ) {}

  private get baseUrl(): string {
    return this.configuration.Server + 'v2/facility/events';
  }

  /**
   * Gets one page of venue events for the context venue.
   *
   * @param page 1-based page number.
   * @param size Page size.
   * @param filter Alert types to show. Empty means all accessible alert types.
   */
  GetVenueEvents = (page: number, size: number, filter: IVenueEventListFilter): Observable<IVenueEventListItem[]> => {
    return this.http.post<IVenueEventListItem[]>(`${this.baseUrl}/${page}/${size}`, filter);
  };

  /**
   * Gets a venue event for the edit form.
   *
   * @param identity The venue event's identity GUID.
   */
  GetVenueEvent = (identity: string): Observable<IVenueEventDetail> => {
    return this.http.get<IVenueEventDetail>(`${this.baseUrl}/${identity}`);
  };

  /**
   * Gets the alert types for the list filter and the form.
   */
  GetAlertTypes = (): Observable<IVenueEventAlertType[]> => {
    return this.http.get<IVenueEventAlertType[]>(`${this.baseUrl}/alert-types`);
  };

  /**
   * Gets the system events offered in the form's Event dropdown.
   *
   * @param venueEventIdentity The venue event being edited, so its own event stays listed.
   */
  GetSystemEvents = (venueEventIdentity?: string): Observable<IVenueEventSystemEventOption[]> => {
    let params = new HttpParams();
    if (venueEventIdentity) {
      params = params.set('venueEventIdentity', venueEventIdentity);
    }
    return this.http.get<IVenueEventSystemEventOption[]>(`${this.baseUrl}/system-events`, { params });
  };

  /**
   * Gets the rows of the Sensor Types dialog for a system event.
   *
   * @param systemEventId The system event picked in the form.
   * @param venueEventIdentity The venue event being edited.
   */
  GetSensorTypes = (systemEventId: number, venueEventIdentity?: string): Observable<IVenueEventSensorTypeOptions> => {
    let params = new HttpParams().set('systemEventId', systemEventId);
    if (venueEventIdentity) {
      params = params.set('venueEventIdentity', venueEventIdentity);
    }
    return this.http.get<IVenueEventSensorTypeOptions>(`${this.baseUrl}/sensor-types`, { params });
  };

  /**
   * Gets the venue's active tasks of an alert type.
   *
   * @param alertTypeId The alert type picked in the form.
   */
  GetTasks = (alertTypeId: number): Observable<IVenueEventTaskOption[]> => {
    const params = new HttpParams().set('alertTypeId', alertTypeId);
    return this.http.get<IVenueEventTaskOption[]>(`${this.baseUrl}/tasks`, { params });
  };

  /**
   * Creates a venue event.
   *
   * @returns The identity of the new venue event.
   */
  CreateVenueEvent = (body: IVenueEventSaveRequest): Observable<string> => {
    return this.http.post<string>(this.baseUrl, body);
  };

  /**
   * Updates a venue event.
   *
   * @param identity The venue event's identity GUID.
   */
  UpdateVenueEvent = (identity: string, body: IVenueEventSaveRequest): Observable<void> => {
    return this.http.put<void>(`${this.baseUrl}/${identity}`, body);
  };

  /**
   * Deletes a venue event with its sensor types and tasks.
   *
   * @param identity The venue event's identity GUID.
   */
  DeleteVenueEvent = (identity: string): Observable<void> => {
    return this.http.delete<void>(`${this.baseUrl}/${identity}`);
  };
}
